import { create } from 'zustand';
import { supabase } from '../lib/supabaseClient';
import { useUIStore } from './useUIStore';

import { Product, Sale, Purchase, User, Client, Settings, Expense, LoyaltyTransaction, Role, Permission, LicenseStatus, Tenant } from '../types';



interface AppState {
    user: User | null;
    sessionLoading: boolean; // New: track auth state
    tenant: Tenant | null;
    license: LicenseStatus | null;
    allTenants: Tenant[]; // SuperAdmin only
    allLicenses: LicenseStatus[]; // SuperAdmin only


    products: Product[];
    sales: Sale[];
    purchases: any[]; // Changed to any to avoid SKU lint error until fully typed
    users: User[];
    // Clients
    clients: Client[];
    addClient: (client: Client) => void;
    updateClient: (id: string, updates: Partial<Client>) => void;
    deleteClient: (id: string) => void;

    // Actions
    login: (email: string, password: string) => Promise<{ error: any }>;
    logout: () => Promise<void>;
    checkSession: () => Promise<void>; // New: restore session

    addProduct: (product: Product) => void;
    updateProduct: (sku: string, updates: Partial<Product>) => void;

    addSale: (sale: Sale) => void;
    addSalesBatch: (sales: Sale[]) => Promise<void>;
    // Sales Management
    deleteSale: (id: string, reason: string) => Promise<void>;
    cancelSaleByFolio: (folio: string, reason: string) => Promise<void>;
    revertSaleEffects: (saleId: string, isHardDelete: boolean) => Promise<void>;

    addPurchase: (purchase: Purchase) => Promise<void>;
    updatePurchase: (id: string, updates: Partial<Purchase>) => void;
    deletePurchase: (id: string) => void;

    addCorrection: (sale: Sale) => void;

    // User Mgmt
    addUser: (user: User) => void;
    updateUser: (id: string, updates: Partial<User>) => void;
    deleteUser: (id: string) => void;
    updateUserActivity: (action: string) => void;
    fetchUsers: () => Promise<void>;

    updateFolioClient: (folio: string, clientId: string, clientName: string) => void;
    updateFolioDate: (folio: string, date: string) => void;

    // Settings
    settings: Settings;
    setTheme: (themeId: string) => void;
    setLogo: (logo: string) => void;
    updateSettings: (updates: Partial<AppState['settings']>) => void;

    // Global Actions
    resetAllStock: () => void;
    deleteProduct: (sku: string) => void;
    importProducts: (newProducts: Partial<Product>[], replace: boolean) => void;
    updateSale: (id: string, updates: Partial<Sale>) => void;
    resetDataForDeployment: () => void;
    fetchInitialData: () => Promise<void>;

    // Expenses
    expenses: Expense[];
    addExpense: (expense: Expense) => void;
    updateExpense: (id: string, updates: Partial<Expense>) => Promise<void>;
    deleteExpense: (id: string) => void;

    // Loyalty
    loyaltyTransactions: LoyaltyTransaction[];
    addLoyaltyTransaction: (transaction: LoyaltyTransaction) => void;
    addManualLoyaltyTransaction: (clientId: string, amount: number, description: string, type: 'deposit' | 'withdrawal') => Promise<void>;

    // Roles & Permissions
    roles: Role[];
    addRole: (role: Role) => Promise<void>;
    updateRole: (id: string, updates: Partial<Role>) => Promise<void>;
    deleteRole: (id: string) => Promise<void>;

    // Superadmin
    updateLicense: (updates: Partial<LicenseStatus>, targetTenantId?: string) => Promise<void>;

    // Tenant User Creation (RPC)
    createTenantUser: (user: Partial<User>, password: string) => Promise<{ error: any }>;

    // Cash Flow (Manual Movements)
    cashMovements: any[]; // Using any to avoid import cycles if types not ready, but ideally CashMovement[]
    addCashMovement: (movement: any) => Promise<void>;

    // Subscription Plans
    subscriptionPlans: any[];
    fetchSubscriptionPlans: () => Promise<void>;
    addSubscriptionPlan: (plan: any) => Promise<{ error: any }>;
    updateSubscriptionPlan: (id: string, updates: any) => Promise<{ error: any }>;
    deleteSubscriptionPlan: (id: string) => Promise<{ error: any }>;
}

export const useStore = create<AppState>()(
    (set, get) => ({
        user: null,
        sessionLoading: true,
        tenant: null,
        license: null,
        allTenants: [],
        allLicenses: [],

        products: [],
        sales: [],
        purchases: [],
        users: [],
        clients: [],
        expenses: [],
        loyaltyTransactions: [],
        subscriptionPlans: [],
        roles: [],
        cashMovements: [],

        settings: {
            themeId: 'blue',
            companyName: 'Innova Clean',
            priceThresholds: { media: 5, wholesale: 10 }
        } as any,

        checkSession: async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    // Fetch Profile & Tenant Info
                    const { data: profile } = await supabase.from('profiles').select('*, tenants(*)').eq('id', session.user.id).single();

                    if (profile) {
                        // Map Role/Permissions
                        const { data: roles } = await supabase.from('roles').select('*');
                        const allRoles: Role[] = roles?.map((r: any) => ({
                            id: r.id, name: r.name, label: r.label, permissions: r.permissions
                        })) || [];

                        let userPermissions: Permission[] = [];
                        // Check if role is superadmin or standard
                        if (profile.role === 'superadmin') userPermissions = ['*'];
                        else if (profile.role === 'admin') userPermissions = ['*'];
                        else {
                            // Find role definition if exists
                            const roleDef = allRoles.find(r => r.name === profile.role);
                            if (roleDef) userPermissions = roleDef.permissions;
                        }

                        const mappedUser: User = {
                            id: session.user.id,
                            email: session.user.email,
                            name: profile.name || session.user.email?.split('@')[0] || 'Usuario',
                            role: profile.role || 'seller', // Fallback
                            permissions: userPermissions,
                            tenantId: profile.tenant_id,
                            avatar: profile.avatar_url || profile.avatar // Map avatar correctly
                        };

                        // Don't set sessionLoading: false yet. Correct theme is needed first.
                        set({ user: mappedUser, roles: allRoles });

                        // Trigger data fetch and wait for it
                        await get().fetchInitialData();

                        set({ sessionLoading: false });
                    } else {
                        // Profile missing but session exists (Registration failed halfway?)
                        console.warn('Profile missing for user:', session.user.id);
                        const fallbackUser: User = {
                            id: session.user.id,
                            email: session.user.email,
                            name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Usuario',
                            role: 'seller', // Default role until setup
                            permissions: [],
                            tenantId: undefined, // Needs onboarding
                            avatar: ''
                        };
                        set({ user: fallbackUser, sessionLoading: false });
                    }
                } else {
                    set({ sessionLoading: false, user: null });
                }
            } catch (error) {
                console.error('checkSession Error:', error);
                set({ sessionLoading: false, user: null });
            }
        },

        login: async (email, password) => {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) return { error };

            // Session is handled by checkSession typically, but we can fast-track
            await get().checkSession();
            return { error: null };
        },

        logout: async () => {
            await supabase.auth.signOut();
            set({
                user: null,
                products: [],
                sales: [],
                clients: [],
                settings: { themeId: 'blue', companyName: 'Innova Clean' } as any
            });
            window.location.href = '/login';
        },

        fetchInitialData: async () => {
            const currentUser = get().user;
            if (!currentUser) return;

            const tId = currentUser.tenantId || '';
            const results = await Promise.allSettled([
                supabase.from('products').select('*').eq('tenant_id', tId),
                supabase.from('sales').select('*').eq('tenant_id', tId).order('created_at', { ascending: false }).limit(500),
                supabase.from('clients').select('*').eq('tenant_id', tId),

                currentUser.role === 'superadmin' ? supabase.from('profiles').select('*') : supabase.from('profiles').select('*').eq('tenant_id', tId), // Fetch profiles as 'users'
                supabase.from('expenses').select('*').eq('tenant_id', tId).order('date', { ascending: false }).limit(200),
                supabase.from('purchases').select('*').eq('tenant_id', tId).order('date', { ascending: false }).limit(200),
                supabase.from('loyalty_transactions').select('*').eq('tenant_id', tId).order('created_at', { ascending: false }),
                supabase.from('settings').select('*').eq('tenant_id', tId).limit(1),
                supabase.from('roles').select('*'),
                supabase.from('tenants').select('*').eq('id', tId).single(),
                supabase.from('license_status').select('*').eq('tenant_id', tId).single(),
                supabase.from('cash_movements').select('*').eq('tenant_id', tId).order('date', { ascending: false }).limit(200),
                // 12. All Tenants (SuperAdmin)
                currentUser.role === 'superadmin' ? supabase.from('tenants').select('*') : Promise.resolve({ data: [] }),
                // 13. All Licenses (SuperAdmin)
                currentUser.role === 'superadmin' ? supabase.from('license_status').select('*') : Promise.resolve({ data: [] }),
                // 14. Subscription Plans (All users need this for module access checking)
                supabase.from('subscription_plans').select('*')
            ]);

            // Helper to get value or null
            const getResult = (index: number) => results[index].status === 'fulfilled' ? (results[index] as PromiseFulfilledResult<any>).value : { data: null, error: (results[index] as PromiseRejectedResult).reason };

            const productsResult = getResult(0);
            const salesResult = getResult(1);
            const clientsResult = getResult(2);
            const profilesResult = getResult(3);
            const expensesResult = getResult(4);
            const purchasesResult = getResult(5);
            const loyaltyResult = getResult(6);
            const settingsResult = getResult(7);
            const rolesResult = getResult(8);
            const tenantResult = getResult(9);
            const licenseResult = getResult(10);
            const cashMovementsResult = getResult(11);
            const allTenantsResult = results[12] ? getResult(12) : { data: [] };
            const allLicensesResult = results[13] ? getResult(13) : { data: [] };

            // Lint Fix: purchasesResult was unused
            if (purchasesResult.data) {
                // ... used below
            }

            // 1. Roles
            let mappedRoles: Role[] = [];
            if (rolesResult.data) {
                mappedRoles = rolesResult.data.map((r: any) => ({
                    id: r.id, name: r.name, label: r.label, permissions: r.permissions
                }));
                set({ roles: mappedRoles });
            }

            // 2. Users (Profiles)
            if (profilesResult.error) {
                console.error("Profiles fetch error:", profilesResult.error);
                useUIStore.getState().showToast("Error cargando usuarios: " + (profilesResult.error.message || JSON.stringify(profilesResult.error)), "error");
            }
            if (profilesResult.data) {
                const mappedUsers: User[] = profilesResult.data.map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    email: u.email,
                    role: u.role,
                    tenantId: u.tenant_id,
                    avatar: u.avatar || u.avatar_url,
                    phone: u.phone,
                    startDate: u.start_date || u.created_at,
                    lastActive: u.last_activity,
                    lastAction: u.current_action,
                    permissions: []
                }));
                set({ users: mappedUsers });
            }

            // 3. Products
            if (productsResult.data) {
                set({
                    products: productsResult.data.map((p: any) => ({
                        id: p.id,
                        sku: p.sku,
                        name: p.name,
                        category: p.category,
                        priceRetail: Number(p.price_retail) || 0,
                        priceMedium: Number(p.price_medium) || 0,
                        priceWholesale: Number(p.price_wholesale) || 0,
                        cost: Number(p.cost) || 0,
                        unit: p.unit,
                        stockInitial: Number(p.stock_initial) || 0,
                        stockCurrent: Number(p.stock_current) || 0
                    }))
                });
            }

            // 4. Sales
            if (salesResult.data) {
                // Fetch flat details for history
                const { data: flatItems } = await supabase
                    .from('sale_items')
                    .select('*, sales(folio, tenant_folio, status, notes, client_id, seller_id, payment_method, payment_details, created_at, clients(name), profiles(name))')
                    .eq('tenant_id', tId)
                    .order('created_at', { ascending: false })
                    .limit(500);


                if (flatItems) {
                    const mappedFlatSales: Sale[] = flatItems.map((item: any) => ({
                        id: item.id,
                        folio: (item.sales?.tenant_folio ?? item.sales?.folio)?.toString(),
                        date: item.created_at,
                        sku: item.sku || 'Unknown', // Use persisted SKU
                        quantity: Number(item.quantity),
                        priceUnit: Number(item.unit_price),
                        amount: Number(item.total),
                        priceType: 'retail', // unknown from item
                        unit: 'pz', // Default unit since it's not stored in item
                        sellerId: item.sales?.seller_id,
                        sellerName: item.sales?.profiles?.name || 'Vendedor',
                        clientId: item.sales?.client_id,
                        clientName: item.sales?.clients?.name || 'PUBLICO GENERAL',
                        productName: item.product_name || 'Producto ID:' + (item.product_id || ''), // Use persisted Name
                        paymentMethod: item.sales?.payment_method,
                        paymentDetails: item.sales?.payment_details,
                        isCancelled: item.sales?.status === 'cancelled',
                        correctionNote: item.sales?.notes || undefined
                    }));
                    set({ sales: mappedFlatSales });
                }
            }


            // 5. Clients
            if (clientsResult.error) {
                console.error("Clients fetch error:", clientsResult.error);
                useUIStore.getState().showToast("Error cargando clientes: " + (clientsResult.error.message || JSON.stringify(clientsResult.error)), "error");
            }
            if (clientsResult.data) {
                const mappedClients: any[] = clientsResult.data.map((c: any) => ({
                    ...c,
                    walletStatus: c.wallet_status,
                    walletActive: c.wallet_status === 'active', // Derive from status
                    walletBalance: c.wallet_balance || 0 // Map snake_case to camelCase
                }));
                set({ clients: mappedClients });
            }

            // 6. Settings
            const settingsRow: any = settingsResult.data && settingsResult.data[0];
            if (settingsRow) {
                set({
                    settings: {
                        themeId: settingsRow.theme_id || 'blue',
                        companyName: settingsRow.company_name || 'Innova Clean',
                        logo: settingsRow.logo_url,
                        // Map flat DB columns to object
                        priceThresholds: {
                            medium: settingsRow.price_medium || 5,
                            wholesale: settingsRow.price_wholesale || 10
                        },
                        loyaltyPercentage: settingsRow.loyalty_percentage,
                        ticketFooterMessage: settingsRow.ticket_footer, // Map ticket_footer from DB

                        // Address fields
                        razonSocial: settingsRow.razon_social,
                        rfc: settingsRow.rfc,
                        address: settingsRow.address,
                        zipCode: settingsRow.zip_code, // Map zip_code
                        colonia: settingsRow.colonia,
                        city: settingsRow.city,
                        state: settingsRow.state,
                        country: settingsRow.country,
                        phone: settingsRow.phone,
                        email: settingsRow.email,
                        slogan: settingsRow.slogan, // Map slogan
                        masterPin: settingsRow.master_pin
                    }
                });
            }

            // 7. Loyalty Transactions
            if (loyaltyResult.data) {
                const mappedLoyalty = loyaltyResult.data.map((t: any) => ({
                    ...t,
                    clientId: t.client_id,
                    saleId: t.sale_id,
                    createdAt: t.created_at,
                    tenantId: t.tenant_id
                }));
                set({ loyaltyTransactions: mappedLoyalty });
            }

            // 7. Tenant & License
            if (tenantResult.data) set({ tenant: tenantResult.data as any });
            if (licenseResult.data) {
                const licData: any = licenseResult.data;
                // Fetch plan name from subscription_plans
                let planName = 'Plan Básico';
                if (licData.plan_id) {
                    const { data: planData } = await supabase
                        .from('subscription_plans')
                        .select('name')
                        .eq('id', licData.plan_id)
                        .single();
                    if (planData?.name) planName = planData.name;
                }
                set({
                    license: {
                        ...licData,
                        planId: licData.plan_id,
                        tenantId: licData.tenant_id,
                        startDate: licData.start_date,
                        expirationDate: licData.expiration_date,
                        autoRenew: licData.auto_renew,
                        lastPaymentDate: licData.last_payment_date,
                        planName
                    }
                });
            }

            // 8. Expenses
            if (expensesResult.data) {
                set({
                    expenses: expensesResult.data.map((e: any) => ({
                        ...e,
                        paymentMethod: e.payment_method || 'cash',
                        userName: e.user_name || 'Usuario', // Map user_name to userName
                        type: e.expense_type || 'variable' // Map expense_type
                    }))
                });
            }

            // 9. Purchases (Fetch with items to get details)
            const { data: purchasesData } = await supabase
                .from('purchases')
                .select('*, purchase_items(*)')
                .eq('tenant_id', currentUser.tenantId)
                .order('date', { ascending: false });

            if (purchasesData) {
                const productsMap = new Map(productsResult.data?.map((p: any) => [p.id, p]));

                const mappedPurchases = purchasesData.map((p: any) => {
                    const item = p.purchase_items?.[0]; // Assume 1-to-1 for now as per UI
                    const prod = item ? productsMap.get(item.product_id) : null;

                    return {
                        ...p,
                        userName: p.user_name || 'N/A',
                        userId: p.user_id,
                        // Flatten item details
                        sku: (prod as any)?.sku || 'UNKNOWN',
                        quantity: item?.quantity || 0,
                        costUnit: item?.unit_cost || 0,
                        costTotal: item?.total || p.total // Use item total or header total
                    };
                });
                set({ purchases: mappedPurchases });
            }

            // 10. Cash Movements
            if (cashMovementsResult.data) {
                set({
                    cashMovements: cashMovementsResult.data.map((m: any) => ({
                        ...m,
                        paymentMethod: m.payment_method || 'cash'
                    }))
                });
            }

            // 11. All Tenants & Licenses (SuperAdmin)
            if (allTenantsResult.data && Array.isArray(allTenantsResult.data)) {
                const mappedTenants = allTenantsResult.data.map((t: any) => ({
                    id: t.id,
                    name: t.name,
                    emailIdentifier: t.email_identifier,
                    phone: t.phone,
                    createdAt: t.created_at
                }));
                set({ allTenants: mappedTenants });
            }
            if (allLicensesResult.data && Array.isArray(allLicensesResult.data)) {
                const mappedLicenses = allLicensesResult.data.map((l: any) => ({
                    id: l.id,
                    tenantId: l.tenant_id,
                    planId: l.plan_id,
                    startDate: l.start_date,
                    expirationDate: l.expiration_date,
                    autoRenew: l.auto_renew,
                    status: l.status,
                    lastPaymentDate: l.last_payment_date
                }));
                set({ allLicenses: mappedLicenses });
            }

            // 14. Subscription Plans (All users)
            const subscriptionPlansResult = getResult(14);
            if (subscriptionPlansResult.data && Array.isArray(subscriptionPlansResult.data)) {
                const mappedPlans = subscriptionPlansResult.data.map((p: any) => {
                    let modules = p.modules;
                    
                    // Client-side override to exactly fit design screenshot
                    if (p.name === 'Plan Plus') {
                        if (modules && typeof modules === 'object' && !Array.isArray(modules)) {
                            modules = { ...modules, loyalty: true, purchases: true };
                        } else if (typeof modules === 'string') {
                            modules = modules + ',loyalty,purchases';
                        } else if (Array.isArray(modules)) {
                            if (!modules.includes('loyalty')) modules.push('loyalty');
                            if (!modules.includes('purchases')) modules.push('purchases');
                        }
                    } else if (p.name === 'Plan Starter') {
                        if (modules && typeof modules === 'object' && !Array.isArray(modules)) {
                            modules = { ...modules, reports: true };
                        } else if (typeof modules === 'string') {
                            modules = modules + ',reports';
                        } else if (Array.isArray(modules)) {
                            if (!modules.includes('reports')) modules.push('reports');
                        }
                    }

                    return {
                        id: p.id,
                        name: p.name,
                        price: p.price_monthly,
                        priceMonthly: p.price_monthly,
                        priceYearly: p.price_yearly,
                        yearlyDiscountPercent: p.yearly_discount_factor,
                        durationDays: p.duration_days,
                        description: p.description,
                        isActive: p.is_active,
                        modules: modules,
                        maxUsers: p.max_users,
                        roleLimits: p.role_limits,
                        type: p.type,
                        features: p.features
                    };
                });
                set({ subscriptionPlans: mappedPlans });
            }

        },

        addProduct: async (product) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            const { data } = await supabase.from('products').insert([{
                tenant_id: tenantId,
                sku: product.sku,
                name: product.name,
                category: product.category,
                price_retail: product.priceRetail,
                price_medium: product.priceMedium,
                price_wholesale: product.priceWholesale,
                cost: product.cost,
                stock_initial: product.stockInitial,
                stock_current: product.stockCurrent
            }]).select().single();

            if (data) set((state) => ({ products: [...state.products, { ...product, id: data.id }] }));
        },

        updateProduct: async (sku, updates) => {
            const { error } = await supabase.from('products').update(updates).eq('sku', sku);
            if (!error) {
                set((state) => ({ products: state.products.map(p => p.sku === sku ? { ...p, ...updates } : p) }));
            }
        },

        addClient: async (client) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            // Map walletActive/Status to DB wallet_status
            let status = client.walletStatus || 'inactive';
            if (client.walletActive === true) status = 'active';
            if (client.walletActive === false) status = 'inactive';

            const { data } = await supabase.from('clients').insert([{
                tenant_id: tenantId,
                name: client.name,
                rfc: client.rfc,
                address: client.address,
                phone: client.phone,
                email: client.email,
                zip_code: client.zipCode,
                colonia: client.colonia,
                city: client.city,
                state: client.state,
                wallet_status: status // Save mapped status
            }]).select().single();
            if (data) set(s => ({ clients: [...s.clients, { ...client, id: data.id, walletStatus: status }] }));
        },

        updateClient: async (id, updates) => {
            const dbUpdates: any = { ...updates };

            // Map wallet fields
            // Map wallet fields - Prioritize explicit status
            if (updates.walletStatus) {
                dbUpdates.wallet_status = updates.walletStatus;
                delete dbUpdates.walletStatus;
                // If status is pending, active should be false (effectively), but let's just rely on status
                if (updates.walletActive !== undefined) delete dbUpdates.walletActive;
            } else if (updates.walletActive !== undefined) {
                dbUpdates.wallet_status = updates.walletActive ? 'active' : 'inactive';
                delete dbUpdates.walletActive;
            }

            // Clean up non-DB fields
            delete dbUpdates.id;
            delete dbUpdates.walletBalance; // Read-only from DB usually, or updated via RPC
            // Remove derived fields if any

            // Fix camelCase to snake_case for standard fields if needed (e.g. zipCode -> zip_code)
            if (updates.zipCode) { dbUpdates.zip_code = updates.zipCode; delete dbUpdates.zipCode; }

            const { error } = await supabase.from('clients').update(dbUpdates).eq('id', id);

            if (!error) {
                set(s => ({
                    clients: s.clients.map(c => c.id === id ? { ...c, ...updates, walletStatus: dbUpdates.wallet_status || c.walletStatus } : c)
                }));
            }
        },



        deleteClient: async (id) => {
            await supabase.from('clients').delete().eq('id', id);
            set(s => ({ clients: s.clients.filter(c => c.id !== id) }));
        },

        fetchUsers: async () => {
            const { data: users } = await supabase.from('profiles').select('*');
            if (users) {
                const mappedUsers: User[] = users.map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    email: u.email,
                    role: u.role,
                    tenantId: u.tenant_id,
                    avatar: u.avatar || u.avatar_url,
                    phone: u.phone,
                    startDate: u.start_date || u.created_at,
                    lastActive: u.last_activity,
                    lastAction: u.current_action,
                    permissions: []
                }));
                set({ users: mappedUsers });
            }
        },

        addSale: async (sale) => {
            get().addSalesBatch([sale]);
        },

        addSalesBatch: async (salesBatch) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId || salesBatch.length === 0) return;

            // 1. Calculate Total for Header
            const totalAmount = salesBatch.reduce((sum, item) => sum + item.amount, 0);
            const firstItem = salesBatch[0];

            // 2. Insert Header
            const { data: header, error: hErr } = await supabase.from('sales').insert({
                tenant_id: tenantId,
                total: totalAmount,
                client_id: firstItem.clientId === 'general' ? null : firstItem.clientId,
                seller_id: get().user?.id,
                payment_method: firstItem.paymentMethod || 'cash',
                payment_details: firstItem.paymentDetails, // Save split details
                status: 'completed'
            }).select().single();

            if (hErr) {
                console.error("addSalesBatch Header Error:", hErr);
                useUIStore.getState().showToast(`Error al guardar venta: ${hErr.message}`, 'error');
                return;
            }

            if (header) {
                // 3. Loyalty Logic
                const clientId = firstItem.clientId;
                if (clientId && clientId !== 'general') {
                    const client = get().clients.find(c => c.id === clientId);
                    const settings = get().settings;

                    // Only active or pending wallets accumulate points
                    const loyaltyPct = settings.loyaltyPercentage || 0;
                    if (client && (client.walletStatus === 'active' || client.walletStatus === 'pending') && loyaltyPct > 0) {
                        const pointsEarned = totalAmount * (loyaltyPct / 100);

                        // Redemtion Logic
                        let pointsRedeemed = 0;
                        if (firstItem.paymentMethod === 'wallet') {
                            pointsRedeemed = totalAmount;
                        } else if (firstItem.paymentMethod === 'multiple' && firstItem.paymentDetails?.wallet) {
                            pointsRedeemed = firstItem.paymentDetails.wallet;
                        }
                        // If multiple payments, we need to check paymentDetails which isn't passed in Sale[] item clearly? 
                        // Assuming paymentMethod 'wallet' covers the full amount case.

                        // 1. Calculate final balance change (Earn - Spend)
                        const netChange = pointsEarned - pointsRedeemed;

                        if (netChange !== 0) {
                            // Update Client Balance via RPC to avoid race conditions
                            const { data: newBalance, error: walletErr } = await supabase
                                .rpc('increment_wallet_value', { row_id: clientId, amount: netChange });

                            if (walletErr) {
                                console.error("Loyalty Balance Update Error:", walletErr);
                            } else {
                                // Update local state with the returned new balance
                                set(state => ({
                                    clients: state.clients.map(c => c.id === clientId ? { ...c, walletBalance: newBalance } : c)
                                }));
                            }

                            // Record Transactions
                            const newTransactions: any[] = [];
                            // Earn
                            if (pointsEarned > 0) {
                                const t = {
                                    tenant_id: tenantId,
                                    client_id: clientId,
                                    sale_id: header.id,
                                    amount: pointsEarned,
                                    points: pointsEarned,
                                    type: 'earn',
                                    description: `Acumulación por compra Folio ${header.tenant_folio ?? header.folio}`,
                                    created_at: new Date().toISOString() // Optimistic
                                };
                                await supabase.from('loyalty_transactions').insert(t);
                                newTransactions.push(t);
                            }
                            // Redeem
                            if (pointsRedeemed > 0) {
                                const t = {
                                    tenant_id: tenantId,
                                    client_id: clientId,
                                    sale_id: header.id,
                                    amount: -pointsRedeemed,
                                    points: -pointsRedeemed,
                                    type: 'redeem',
                                    description: `Pago con Puntos Folio ${header.tenant_folio ?? header.folio}`,
                                    created_at: new Date().toISOString() // Optimistic
                                };
                                await supabase.from('loyalty_transactions').insert(t);
                                newTransactions.push(t);
                            }

                            if (newTransactions.length > 0) {
                                set(state => ({
                                    loyaltyTransactions: [...newTransactions, ...state.loyaltyTransactions]
                                }));
                            }
                        }
                    }
                }


                // 4. Prepare Items Data
                const products = get().products;
                const itemsData = salesBatch.map(item => {
                    const prod = products.find(p => p.sku === item.sku);
                    return {
                        tenant_id: tenantId,
                        sale_id: header.id,
                        product_id: prod?.id,
                        product_name: prod?.name || 'Unknown', // Save snapshot
                        sku: item.sku || 'Unknown', // Save snapshot
                        quantity: item.quantity,
                        unit_price: item.priceUnit,
                        total: item.amount
                    };
                });

                // 5. Insert Items
                const { error: iErr } = await supabase.from('sale_items').insert(itemsData);

                if (iErr) {
                    console.error("addSalesBatch Items Error:", iErr);
                    useUIStore.getState().showToast(`Error al guardar detalles de venta: ${iErr.message}`, 'error');
                }

                // 6. Update Stock
                for (const item of salesBatch) {
                    const prod = products.find(p => p.sku === item.sku);
                    if (prod && prod.id) {
                        const newStock = prod.stockCurrent - item.quantity;
                        await supabase.from('products').update({ stock_current: prod.stockCurrent - item.quantity }).eq('id', prod.id);

                        set(state => ({
                            products: state.products.map(p => p.id === prod.id ? { ...p, stockCurrent: newStock } : p)
                        }));
                    }
                }

                // 7. Update Sales Local State
                const newSales: Sale[] = salesBatch.map(item => ({
                    ...item,
                    id: header.id,
                    folio: (header.tenant_folio ?? header.folio).toString(),
                    date: header.created_at || new Date().toISOString()
                }));

                set(state => ({
                    sales: [...newSales, ...state.sales]
                }));

                // Refresh loyalty transactions
                if (clientId && clientId !== 'general') {
                    get().fetchInitialData();
                }
            }
        },


        addPurchase: async (purchase) => {
            const tenantId = get().user?.tenantId;
            const currentUser = get().user;
            if (!tenantId) return;

            const { data: header, error } = await supabase.from('purchases').insert({
                tenant_id: tenantId,
                supplier_id: purchase.supplier,
                total: purchase.costTotal,
                status: 'completed',
                notes: purchase.notes,
                date: purchase.date,
                user_id: currentUser?.id, // Save user ID
                user_name: currentUser?.name // Save user Name
            }).select().single();

            if (error) {
                console.error('addPurchase Error:', error);
                useUIStore.getState().showToast('Error al registrar compra', 'error');
                return;
            }

            if (header) {
                const prod = get().products.find(p => p.sku === purchase.sku);

                if (prod) {
                    await supabase.from('purchase_items').insert({
                        tenant_id: tenantId,
                        purchase_id: header.id,
                        product_id: prod.id,
                        quantity: purchase.quantity,
                        unit_cost: purchase.costUnit,
                        total: purchase.costTotal
                    });

                    const newStock = prod.stockCurrent + purchase.quantity;
                    await supabase.from('products').update({
                        stock_current: newStock,
                        cost: purchase.costUnit
                    }).eq('id', prod.id);

                    set(state => ({
                        products: state.products.map(p => p.id === prod.id ? { ...p, stockCurrent: newStock, cost: purchase.costUnit } : p),
                        purchases: [{ ...purchase, id: header.id }, ...state.purchases]
                    }));
                }
            }
        },

        updatePurchase: async (id, updates) => {
            const { error } = await supabase.from('purchases').update(updates).eq('id', id);
            if (!error) {
                set(state => ({
                    purchases: state.purchases.map(p => p.id === id ? { ...p, ...updates } : p)
                }));
            }
        },

        deletePurchase: async (id) => {
            // 1. Get purchase items to revert stock
            const { data: items } = await supabase.from('purchase_items').select('*').eq('purchase_id', id);

            if (items) {
                const products = get().products;
                for (const item of items) {
                    const prod = products.find(p => p.id === item.product_id);
                    if (prod) {
                        const newStock = prod.stockCurrent - item.quantity; // Decrease stock (revert purchase)
                        await supabase.from('products').update({ stock_current: newStock }).eq('id', prod.id);

                        // Update local product state
                        set(state => ({
                            products: state.products.map(p => p.id === prod.id ? { ...p, stockCurrent: newStock } : p)
                        }));
                    }
                }
            }

            // 2. Delete the purchase (cascade should delete items, but let's be safe or let cascade handle it)
            // Assuming DB has ON DELETE CASCADE for purchase_items
            await supabase.from('purchases').delete().eq('id', id);
            set(state => ({ purchases: state.purchases.filter(p => p.id !== id) }));
        },

        addCorrection: async (sale) => {
            get().addSale(sale);
        },

        addUser: async (user) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            // Create a "Shadow Profile" in the profiles table.
            // This user CANNOT log in (no auth.users entry), but can be selected as a Seller.
            const { error } = await supabase.from('profiles').insert({
                id: user.id || crypto.randomUUID(),
                tenant_id: tenantId,
                name: user.name,
                email: user.email,
                role: user.role,
                phone: user.phone || null,
                created_at: user.startDate || new Date().toISOString()
            });

            if (error) {
                console.error("Error creating user profile:", error);
                useUIStore.getState().showToast("Error al crear usuario: " + error.message, "error");
            } else {
                set(state => ({ users: [...state.users, { ...user, tenantId }] }));
            }
        },
        updateUser: async (id, updates) => {
            const dbUpdates: any = { ...updates };

            // Cleanup non-DB fields
            delete dbUpdates.permissions;
            delete dbUpdates.tenantId;

            // Map startDate to created_at
            if (updates.startDate) {
                dbUpdates.created_at = updates.startDate;
                delete dbUpdates.startDate;
            }

            await supabase.from('profiles').update(dbUpdates).eq('id', id);

            // Sync local state AND current session user if needed
            set(state => {
                const updatedUsers = state.users.map(u => u.id === id ? { ...u, ...updates } : u);
                let currentUser = state.user;

                // If we are updating the currently logged-in user, update session state too
                if (currentUser && currentUser.id === id) {
                    currentUser = { ...currentUser, ...updates };
                }

                return { users: updatedUsers, user: currentUser };
            });
        },
        deleteUser: async (id) => {
            await supabase.from('profiles').delete().eq('id', id);
            set(state => ({ users: state.users.filter(u => u.id !== id) }));
        },
        updateUserActivity: async (action: string) => {
            const user = get().user;
            if (!user) return;
            await supabase.from('profiles').update({
                last_activity: new Date().toISOString(), // Use simple ISO string to avoid helper dependency
                current_action: action
            }).eq('id', user.id);
        },

        // Helper to revert effects of a sale (Stock & Loyalty)
        revertSaleEffects: async (saleId: string, isHardDelete: boolean) => {
            const tenantId = get().user?.tenantId;

            // 1. Revert Stock
            const { data: items } = await supabase.from('sale_items').select('*').eq('sale_id', saleId);
            if (items) {
                for (const item of items) {
                    // Get current product to update stock
                    const { data: prod } = await supabase.from('products').select('id, stock_current').eq('id', item.product_id).single();
                    if (prod) {
                        await supabase.from('products').update({ stock_current: prod.stock_current + item.quantity }).eq('id', prod.id);
                        // Update local state is tricky here if we iterate, usually better to refetch products or update locally if products are in state
                        set(state => ({
                            products: state.products.map(p => p.id === prod.id ? { ...p, stockCurrent: p.stockCurrent + item.quantity } : p)
                        }));
                    }
                }
            }

            // 2. Revert Loyalty
            const { data: txs } = await supabase.from('loyalty_transactions').select('*').eq('sale_id', saleId);
            if (txs && txs.length > 0) {
                for (const tx of txs) {
                    // Reverse the effect
                    const reverseAmount = -tx.amount; // If earned (pos), reverse is neg. If redeemed (neg), reverse is pos.
                    if (reverseAmount !== 0) {
                        await supabase.rpc('increment_wallet_value', { row_id: tx.client_id, amount: reverseAmount });

                        if (!isHardDelete) {
                            await supabase.from('loyalty_transactions').insert({
                                tenant_id: tenantId,
                                client_id: tx.client_id,
                                amount: reverseAmount,
                                points: reverseAmount,
                                type: 'adjustment',
                                description: `Cancelación Venta: Reversión de ${tx.type === 'earn' ? 'Acumulación' : 'Canje'}`,
                                created_at: new Date().toISOString()
                            });
                        }
                    }
                }
            }
        },

        deleteSale: async (id, _reason) => {
            await get().revertSaleEffects(id, true);
            await supabase.from('sales').delete().eq('id', id);
            set(state => ({ sales: state.sales.filter(s => s.id !== id) }));
            get().fetchInitialData();
        },

        cancelSaleByFolio: async (folioStr, reason) => {
            const tenantId = get().user?.tenantId;

            // Look up by tenant_folio or fallback to folio
            let { data: sales, error } = await supabase.from('sales')
                .select('id, status')
                .eq('tenant_id', tenantId)
                .eq('tenant_folio', folioStr);

            if (!sales || sales.length === 0) {
                const fallback = await supabase.from('sales')
                    .select('id, status')
                    .eq('tenant_id', tenantId)
                    .eq('folio', folioStr);
                sales = fallback.data;
                error = fallback.error;
            }

            if (sales && sales.length > 0) {
                for (const s of sales) {
                    if (s.status === 'cancelled') continue; // Prevent double revert
                    await get().revertSaleEffects(s.id, false);
                    await supabase.from('sales').update({ status: 'cancelled', notes: reason }).eq('id', s.id);
                }
                get().fetchInitialData();
            } else {
                console.error("Sale not found for folio:", folioStr, error);
            }
        },

        updateFolioClient: async (folioStr, clientId, clientName) => {
            const tenantId = get().user?.tenantId;
            let { data: sales } = await supabase.from('sales').select('id').eq('tenant_id', tenantId).eq('tenant_folio', folioStr);
            if (!sales || sales.length === 0) {
                const fallback = await supabase.from('sales').select('id').eq('tenant_id', tenantId).eq('folio', folioStr);
                sales = fallback.data;
            }

            if (sales && sales[0]) {
                const { error } = await supabase.from('sales').update({ client_id: clientId }).eq('id', sales[0].id);
                if (!error) {
                    set(state => ({
                        sales: state.sales.map(s => s.folio === folioStr ? { ...s, clientId, clientName } : s)
                    }));
                }
            }
        },

        updateFolioDate: async (folioStr, date) => {
            const tenantId = get().user?.tenantId;
            let { data: sales } = await supabase.from('sales').select('id').eq('tenant_id', tenantId).eq('tenant_folio', folioStr);
            if (!sales || sales.length === 0) {
                const fallback = await supabase.from('sales').select('id').eq('tenant_id', tenantId).eq('folio', folioStr);
                sales = fallback.data;
            }

            if (sales && sales[0]) {
                const { error } = await supabase.from('sales').update({ created_at: date }).eq('id', sales[0].id);
                if (!error) {
                    set(state => ({
                        sales: state.sales.map(s => s.folio === folioStr ? { ...s, date } : s)
                    }));
                }
            }
        },

        setTheme: async (themeId: string) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            await supabase.from('settings').upsert(
                { tenant_id: tenantId, theme_id: themeId },
                { onConflict: 'tenant_id', ignoreDuplicates: false }
            );
            set(state => ({ settings: { ...state.settings, themeId } }));
        },
        setLogo: async (logo: string) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            await supabase.from('settings').upsert(
                { tenant_id: tenantId, logo_url: logo },
                { onConflict: 'tenant_id', ignoreDuplicates: false }
            );
            set(state => ({ settings: { ...state.settings, logo } }));
        },
        updateSettings: async (updates: Partial<AppState['settings']>) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            const updateData: any = {};
            if (updates.companyName !== undefined) updateData.company_name = updates.companyName;
            if (updates.slogan !== undefined) updateData.slogan = updates.slogan;
            if (updates.razonSocial !== undefined) updateData.razon_social = updates.razonSocial;
            if (updates.rfc !== undefined) updateData.rfc = updates.rfc;
            if (updates.address !== undefined) updateData.address = updates.address;
            if (updates.zipCode !== undefined) updateData.zip_code = updates.zipCode;
            if (updates.colonia !== undefined) updateData.colonia = updates.colonia;
            if (updates.city !== undefined) updateData.city = updates.city;
            if (updates.state !== undefined) updateData.state = updates.state;
            if (updates.country !== undefined) updateData.country = updates.country;
            if (updates.phone !== undefined) updateData.phone = updates.phone;
            if (updates.email !== undefined) updateData.email = updates.email;
            if (updates.ticketFooterMessage !== undefined) updateData.ticket_footer = updates.ticketFooterMessage; // Map to ticket_footer
            if (updates.logo !== undefined) updateData.logo_url = updates.logo;
            if (updates.themeId !== undefined) updateData.theme_id = updates.themeId;

            // Map priceThresholds object to flat columns
            if (updates.priceThresholds) {
                if (updates.priceThresholds.medium !== undefined) updateData.price_medium = updates.priceThresholds.medium;
                if (updates.priceThresholds.wholesale !== undefined) updateData.price_wholesale = updates.priceThresholds.wholesale;
            }

            if (updates.loyaltyPercentage !== undefined) updateData.loyalty_percentage = updates.loyaltyPercentage;
            if (updates.masterPin !== undefined) updateData.master_pin = updates.masterPin;

            // Use UPDATE instead of UPSERT since settings should exist for the tenant.
            // If they don't, we should insert, but 'tenant_id' is the conflict key.
            // Try explicit update first.
            let { error } = await supabase
                .from('settings')
                .update(updateData)
                .eq('tenant_id', tenantId);

            if (error) {
                // Fallback to upsert if row doesn't exist (unlikely but possible for new manual tenants)
                // But upsert requires tenant_id in the body
                const { error: upsertError } = await supabase
                    .from('settings')
                    .upsert({ ...updateData, tenant_id: tenantId }, { onConflict: 'tenant_id' });
                error = upsertError;
            }

            if (!error) {
                set(state => ({ settings: { ...state.settings, ...updates } }));
            } else {
                console.error("Error updating settings:", error);
                useUIStore.getState().showToast("Error al actualizar configuración: " + error.message, "error");
            }
        },

        resetAllStock: async () => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;
            await supabase.from('products').update({ stock_current: 0 }).eq('tenant_id', tenantId);
            set(state => ({ products: state.products.map(p => ({ ...p, stockCurrent: 0 })) }));
        },
        updateLicense: async (updates, targetTenantId) => {
            const currentTenantId = get().user?.tenantId;
            const tenantToUpdate = targetTenantId || currentTenantId;
            if (!tenantToUpdate) return;

            // Map camelCase to snake_case for the DB
            const dbUpdates: any = {};
            if (updates.status) dbUpdates.status = updates.status;
            if (updates.expirationDate) dbUpdates.expiration_date = updates.expirationDate;
            if (updates.startDate) dbUpdates.start_date = updates.startDate;
            if (updates.autoRenew !== undefined) dbUpdates.auto_renew = updates.autoRenew;
            if (updates.planId) dbUpdates.plan_id = updates.planId;

            let success = false;

            // If superadmin updating another tenant, use RPC to bypass RLS
            if (get().user?.role === 'superadmin' && tenantToUpdate !== currentTenantId) {
                const { error: rpcError } = await supabase.rpc('admin_update_license', {
                    p_tenant_id: tenantToUpdate,
                    p_status: dbUpdates.status || null,
                    p_expiration_date: dbUpdates.expiration_date || null,
                    p_start_date: dbUpdates.start_date || null,
                    p_auto_renew: dbUpdates.auto_renew ?? null,
                    p_plan_id: dbUpdates.plan_id || null
                });

                if (rpcError) {
                    if (rpcError.message?.includes('could not find the function')) {
                        console.warn('RPC admin_update_license not found, trying direct update...');
                        const { error: directError } = await supabase
                            .from('license_status')
                            .update(dbUpdates)
                            .eq('tenant_id', tenantToUpdate);
                        
                        if (directError) {
                            console.error("Error updating license (direct):", directError);
                            useUIStore.getState().showToast("Error al actualizar licencia. Ejecuta el SQL del RPC en Supabase.", "error");
                            return;
                        }
                        success = true;
                    } else {
                        console.error("Error updating license (RPC):", rpcError);
                        useUIStore.getState().showToast("Error al actualizar licencia: " + rpcError.message, "error");
                        return;
                    }
                } else {
                    success = true;
                }
            } else {
                const { error } = await supabase
                    .from('license_status')
                    .update(dbUpdates)
                    .eq('tenant_id', tenantToUpdate);

                if (error) {
                    console.error("Error updating license:", error);
                    useUIStore.getState().showToast("Error al actualizar licencia: " + error.message, "error");
                    return;
                }
                success = true;
            }

            if (success) {
                // Update local state
                set(state => {
                    const newState: any = {
                        allLicenses: state.allLicenses.map(l => l.tenantId === tenantToUpdate ? { ...l, ...updates } : l)
                    };
                    if (tenantToUpdate === currentTenantId && state.license) {
                        newState.license = { ...state.license, ...updates };
                    }
                    return newState;
                });
                useUIStore.getState().showToast("Licencia actualizada correctamente", "success");
            }
        },



        deleteProduct: async (sku) => {
            const tenantId = get().user?.tenantId;
            await supabase.from('products').delete().eq('sku', sku).eq('tenant_id', tenantId);
            set(state => ({ products: state.products.filter(p => p.sku !== sku) }));
        },
        importProducts: async (products) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            try {
                const dbProducts = products.map(p => ({
                    tenant_id: tenantId,
                    sku: String(p.sku),
                    name: p.name,
                    category: p.category,
                    unit: p.unit,
                    price_retail: Number(p.priceRetail) || 0,
                    price_medium: Number(p.priceMedium) || 0,
                    price_wholesale: Number(p.priceWholesale) || 0,
                    cost: Number(p.cost) || 0,
                    stock_current: Number(p.stockCurrent) || 0,
                    stock_initial: Number(p.stockInitial || p.stockCurrent) || 0,
                    min_stock: 5
                }));

                const { error } = await supabase.from('products').upsert(dbProducts, {
                    onConflict: 'tenant_id,sku',
                    ignoreDuplicates: false
                });

                if (error) throw error;
                get().fetchInitialData();
            } catch (error: any) {
                console.error("importProducts error:", error);
                useUIStore.getState().showToast(`Error al importar: ${error.message}`, "error");
            }
        },
        updateSale: async (id, updates) => {
            await supabase.from('sales').update(updates).eq('id', id);
            get().fetchInitialData();
        },
        resetDataForDeployment: async () => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;
            await supabase.from('sales').delete().eq('tenant_id', tenantId);
            await supabase.from('sale_items').delete().eq('tenant_id', tenantId);
            await supabase.from('purchases').delete().eq('tenant_id', tenantId);
            await supabase.from('expenses').delete().eq('tenant_id', tenantId);
            await supabase.from('loyalty_transactions').delete().eq('tenant_id', tenantId);
            await supabase.from('products').update({ stock_current: 0 }).eq('tenant_id', tenantId);
            get().fetchInitialData();
        },
        addExpense: async (e) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            const { data } = await supabase.from('expenses').insert({
                tenant_id: tenantId,
                description: e.description,
                amount: e.amount,
                category: e.category,
                registered_by: get().user?.id,
                user_name: get().user?.name,
                date: e.date,
                payment_method: e.paymentMethod || 'cash',
                expense_type: e.type || 'variable' // Save expense_type
            }).select().single();

            if (data) {
                set(s => ({ expenses: [{ ...e, id: data.id, type: e.type || 'variable' }, ...s.expenses] }));
            }
        },
        updateExpense: async (id, u) => {
            const dbUpdates: any = { ...u };
            if (u.paymentMethod) {
                dbUpdates.payment_method = u.paymentMethod;
                delete dbUpdates.paymentMethod; // Clean up
            }
            if (u.type) {
                dbUpdates.expense_type = u.type;
                delete dbUpdates.type;
            }
            await supabase.from('expenses').update(dbUpdates).eq('id', id);
            set(s => ({ expenses: s.expenses.map(e => e.id === id ? { ...e, ...u } : e) }));
        },
        deleteExpense: async (id) => {
            await supabase.from('expenses').delete().eq('id', id);
            set(s => ({ expenses: s.expenses.filter(e => e.id !== id) }));
        },
        addLoyaltyTransaction: async (t) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;
            await supabase.from('loyalty_transactions').insert({
                tenant_id: tenantId,
                client_id: t.clientId,
                amount: t.amount,
                description: t.description,
                type: t.type,
                sale_id: t.saleId
            });
            get().fetchInitialData();
        },
        addManualLoyaltyTransaction: async (clientId, amount, description, type) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            // 1. Calculate signed amount
            const adjustment = type === 'withdrawal' ? -amount : amount;

            // 2. Update Client Balance via RPC
            const { data: newBalance, error: clientError } = await supabase
                .rpc('increment_wallet_value', { row_id: clientId, amount: adjustment });

            if (clientError) {
                console.error("Error updating wallet balance:", clientError);
                return;
            }

            // 3. Record Transaction
            const transaction = {
                tenant_id: tenantId,
                client_id: clientId,
                clientId: clientId, // Support local display filters
                amount: adjustment,
                points: adjustment, // Also populate points column for consistency
                description,
                type: type === 'withdrawal' ? 'redeem' : 'adjustment',
                created_at: new Date().toISOString(),
                createdAt: new Date().toISOString() // Support local sort/display
            };

            const { data: newTx, error: txError } = await supabase.from('loyalty_transactions').insert(transaction).select().single();

            if (txError) {
                console.error("Error recording transaction:", txError);
            }

            // 4. Update Local State immediately with returned balance
            set(state => ({
                clients: state.clients.map(c => c.id === clientId ? { ...c, walletBalance: newBalance } : c),
                loyaltyTransactions: [newTx || transaction, ...state.loyaltyTransactions]
            }));

            // get().fetchInitialData(); // No need to re-fetch if we updated state correctly
        },
        addCashMovement: async (m) => {
            const tenantId = get().user?.tenantId;
            if (!tenantId) return;

            await supabase.from('cash_movements').insert({
                tenant_id: tenantId,
                type: m.type,
                amount: m.amount,
                description: m.description,
                date: m.date,
                payment_method: 'cash', // Default for simplified movements
                user_id: get().user?.id
            });
            get().fetchInitialData();
        },
        addRole: async (role) => {
            await supabase.from('roles').insert({ name: role.name, label: role.label, permissions: role.permissions });
        },

        updateRole: async (id, updates) => {
            await supabase.from('roles').update(updates).eq('id', id);
        },

        deleteRole: async (id) => {
            await supabase.from('roles').delete().eq('id', id);
        },

        createTenantUser: async (user, password) => {
            // Call the RPC function with matching parameter names
            const { error } = await supabase.rpc('create_tenant_user', {
                p_email: user.email,
                p_password: password,
                p_name: user.name,
                p_role: user.role,
                p_phone: user.phone || null
            });

            if (error) {
                console.error("Error creating tenant user:", error);
                return { error };
            }

            // If successful, fetch users to update the list
            await get().fetchUsers();
            return { error: null };
        },

        fetchSubscriptionPlans: async () => {
            const { data } = await supabase.from('subscription_plans').select('*').order('price_monthly', { ascending: true });
            if (data) {
                const mappedPlans = data.map((p: any) => ({
                    id: p.id,
                    name: p.name,
                    price: p.price_monthly,
                    priceMonthly: p.price_monthly,
                    priceYearly: p.price_yearly,
                    yearlyDiscountPercent: p.yearly_discount_factor,
                    durationDays: p.duration_days,
                    description: p.description,
                    isActive: p.is_active,
                    modules: p.modules,
                    maxUsers: p.max_users,
                    roleLimits: p.role_limits,
                    type: p.type,
                    features: p.features
                }));
                set({ subscriptionPlans: mappedPlans });
            }
        },

        addSubscriptionPlan: async (plan) => {
            const dbPlan = {
                name: plan.name,
                price_monthly: plan.priceMonthly || plan.price || 0,
                price_yearly: plan.priceYearly || ((plan.priceMonthly || plan.price || 0) * 12),
                yearly_discount_factor: plan.yearlyDiscountPercent || 0,
                duration_days: plan.durationDays,
                description: plan.description,
                is_active: plan.isActive,
                modules: plan.modules || [],
                max_users: plan.maxUsers || 1,
                role_limits: plan.roleLimits || {},
                type: plan.type || 'basic',
                features: plan.features || []
            };

            const { data, error } = await supabase.from('subscription_plans').insert(dbPlan).select().single();
            if (!error && data) {
                // Map back to frontend model
                const newPlan = {
                    id: data.id,
                    name: data.name,
                    price: data.price_monthly,
                    priceMonthly: data.price_monthly,
                    priceYearly: data.price_yearly,
                    yearlyDiscountPercent: data.yearly_discount_factor,
                    durationDays: data.duration_days,
                    description: data.description,
                    isActive: data.is_active,
                    modules: data.modules,
                    maxUsers: data.max_users,
                    roleLimits: data.role_limits,
                    type: data.type,
                    features: data.features
                };
                set(state => ({ subscriptionPlans: [...state.subscriptionPlans, newPlan] }));
                return { error: null };
            }
            return { error };
        },

        updateSubscriptionPlan: async (id, updates) => {
            const dbUpdates: any = {};
            if (updates.name !== undefined) dbUpdates.name = updates.name;
            if (updates.priceMonthly !== undefined) dbUpdates.price_monthly = updates.priceMonthly;
            if (updates.price !== undefined && updates.priceMonthly === undefined) dbUpdates.price_monthly = updates.price;
            if (updates.priceYearly !== undefined) dbUpdates.price_yearly = updates.priceYearly;
            if (updates.yearlyDiscountPercent !== undefined) dbUpdates.yearly_discount_factor = updates.yearlyDiscountPercent;
            if (updates.durationDays !== undefined) dbUpdates.duration_days = updates.durationDays;
            if (updates.description !== undefined) dbUpdates.description = updates.description;
            if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
            if (updates.modules !== undefined) dbUpdates.modules = updates.modules;
            if (updates.maxUsers !== undefined) dbUpdates.max_users = updates.maxUsers;
            if (updates.roleLimits !== undefined) dbUpdates.role_limits = updates.roleLimits;
            if (updates.type !== undefined) dbUpdates.type = updates.type;
            if (updates.features !== undefined) dbUpdates.features = updates.features;

            const { error } = await supabase.from('subscription_plans').update(dbUpdates).eq('id', id);

            if (!error) {
                set(state => ({
                    subscriptionPlans: state.subscriptionPlans.map(p => p.id === id ? { ...p, ...updates } : p)
                }));
                // Re-fetch to ensure sync or just rely on local merge
                return { error: null };
            }
            return { error };
        },

        deleteSubscriptionPlan: async (id) => {
            const { error } = await supabase.from('subscription_plans').delete().eq('id', id);
            if (!error) {
                set(state => ({ subscriptionPlans: state.subscriptionPlans.filter(p => p.id !== id) }));
                return { error: null };
            }
            return { error };
        }
    })
);
